import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from './AppHeader';
import RechargeModal from './RechargeModal';
import Footer from './Footer';

// Cost fetched from backend; default fallback
const DEFAULT_MERGE_COST = 4;

// Backend API URL - configurable per environment
// Preferred: REACT_APP_PUBLIC_API_URL
// Fallback: production URL for prod builds, localhost for dev
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

const MergePage = () => {
  const navigate = useNavigate();
  const { logout: logoutAuth, user, isAuthenticated } = useAuth();
  
  // Wrapper for logout that redirects to home
  const handleLogout = async () => {
    await logoutAuth();
    navigate('/');
  };
  
  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [firstFile, setFirstFile] = useState(null);
  const [secondFile, setSecondFile] = useState(null);
  const [firstPreview, setFirstPreview] = useState(null);
  const [secondPreview, setSecondPreview] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [mergeCost, setMergeCost] = useState(DEFAULT_MERGE_COST);
  const [isGenerating, setIsGenerating] = useState(false);
  const [mergeError, setMergeError] = useState(null); // Error state for image merging
  const [style, setStyle] = useState('realistic');
  // Default base prompt for merging jewelry (Image A) onto a model (Image B)
  const BASE_MERGE_PROMPT = `Seamlessly place the jewelry from the first image onto the appropriate body part of the person in the second image. Create a professional, high-end fashion photoshoot composition with these specifications:

Placement & Realism:

Ensure perfect size scaling and natural positioning
Match lighting direction and intensity from the original person image
Blend shadows and reflections realistically
Maintain jewelry's original material properties (shine, texture, color)

Photography Quality:

Use optimal angle that showcases both the jewelry and model effectively
Apply professional portrait lighting (soft, even illumination)
Ensure sharp focus on both subject and jewelry
Create natural depth of field with subtle background blur

Composition & Style:

Frame for maximum visual impact (consider close-up or mid-shot as appropriate)
Use flattering angles that complement the model's features
Apply high-end fashion photography aesthetics
Ensure clean, uncluttered background

Technical Specifications:

Output in high resolution (minimum 1024x1024)
Professional color grading and contrast
Eliminate any artifacts or unnatural blending
Maintain photorealistic quality throughout

The final result should look like an authentic luxury jewelry advertisement shot by a professional photographer.`;

  const [prompt, setPrompt] = useState(BASE_MERGE_PROMPT);
  const [jewelryCategory, setJewelryCategory] = useState('');
  // AI merge only (no manual sliders)
  const [walletInfo, setWalletInfo] = useState({ credits: 0, totalSpent: 0 });
  const [userStats, setUserStats] = useState({ imagesGenerated: 0 });
  const hasFetchedMergeCostsRef = useRef(false);

  const firstInputRef = useRef(null);
  const secondInputRef = useRef(null);

  const onDropFirst = useCallback((acceptedFiles) => {
    const f = acceptedFiles?.[0];
    if (!f) return;
    setFirstFile(f);
    const reader = new FileReader();
    reader.onload = () => setFirstPreview(reader.result);
    reader.readAsDataURL(f);
    toast.success('First image uploaded');
  }, []);

  const onDropSecond = useCallback((acceptedFiles) => {
    const f = acceptedFiles?.[0];
    if (!f) return;
    setSecondFile(f);
    const reader = new FileReader();
    reader.onload = () => setSecondPreview(reader.result);
    reader.readAsDataURL(f);
    toast.success('Second image uploaded');
  }, []);

  const dzOptions = {
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
    noClick: false,
    noKeyboard: false,
    multiple: false,
  };

  const dzFirst = useDropzone({ ...dzOptions, onDrop: onDropFirst });
  const dzSecond = useDropzone({ ...dzOptions, onDrop: onDropSecond });

  useEffect(() => {
    if (hasFetchedMergeCostsRef.current) return;
    hasFetchedMergeCostsRef.current = true;
    (async () => {
      try {
        // Prefer per-user effective cost when authenticated
        const token = localStorage.getItem('authToken');
        const endpoint = token ? `${BACKEND_URL}/api/costs/effective` : `${BACKEND_URL}/api/costs`;
        const { data } = await axios.get(endpoint, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
        if (data?.success) setMergeCost(data.data.mergeGenerationCost ?? DEFAULT_MERGE_COST);
      } catch (_) {}
    })();
  }, []);

  // Build a contextual prompt to blend the jewelry (A) onto model (B)
  // Return the exact, full specification the user provided (no extra lines)
  const generateMergePromptByCategory = useCallback((_category) => {
    return BASE_MERGE_PROMPT;
  }, [BASE_MERGE_PROMPT]);

  // Update prompt whenever the selected category changes
  useEffect(() => {
    if (jewelryCategory) {
      setPrompt(generateMergePromptByCategory(jewelryCategory));
    }
  }, [jewelryCategory, generateMergePromptByCategory]);

  // Load wallet credits and profile stats for header
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const [walletRes, profileRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/wallet`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${BACKEND_URL}/api/profile`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (walletRes?.data?.success) setWalletInfo(walletRes.data.data);
        if (profileRes?.data?.success) {
          setUserStats({ imagesGenerated: profileRes.data.data.imagesGenerated });
        }
      } catch (_) {}
    })();
  }, []);

  const handleGenerate = async () => {
    if (!firstFile || !secondFile) {
      toast.error('Please upload both images');
      return;
    }
    setIsGenerating(true);
    setResultImage(null);
    setMergeError(null); // Clear previous errors
    try {
      const form = new FormData();
      form.append('imageA', firstFile);
      form.append('imageB', secondFile);
      form.append('style', style);
      form.append('prompt', prompt);
      if (jewelryCategory) form.append('jewelryCategory', jewelryCategory);
      // Always use AI merge on the backend
      form.append('aiMerge', 'true');
      const token = localStorage.getItem('authToken');
      const response = await axios.post(`${BACKEND_URL}/api/merge-images`, form, {
        headers: { 'Content-Type': 'multipart/form-data', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        timeout: 60000,
      });
      if (response.data?.success) {
        setResultImage(response.data.data.resultImage);
        if (response.data.data.wallet) setWalletInfo(response.data.data.wallet);
        toast.success(`Merged image created (Cost: ${mergeCost} credits)`);
      } else {
        throw new Error(response.data?.error || 'Failed to merge');
      }
    } catch (err) {
      console.error('Merge failed', err);
      
      // Helper function to convert technical errors to user-friendly messages
      const getUserFriendlyError = (error) => {
        const errMsg = error?.response?.data?.error || error?.message || '';
        const errCode = error?.code || error?.response?.status;
        
        // Server error messages
        if (errMsg.toLowerCase().includes('insufficient credits')) {
          return 'You don\'t have enough credits. Please recharge your wallet to continue.';
        }
        if (errMsg.toLowerCase().includes('failed to process') || errMsg.toLowerCase().includes('corrupted') || errMsg.toLowerCase().includes('unsupported format')) {
          return 'We couldn\'t process one of your images. Please make sure both images are valid (JPEG, PNG, GIF, or WebP) and try again.';
        }
        if (errMsg.toLowerCase().includes('jewelry image') || errMsg.toLowerCase().includes('imagea')) {
          return 'The jewelry image couldn\'t be processed. Please try a different image file.';
        }
        if (errMsg.toLowerCase().includes('model image') || errMsg.toLowerCase().includes('imageb')) {
          return 'The model image couldn\'t be processed. Please try a different image file.';
        }
        if (errMsg.toLowerCase().includes('image merging timed out') || errCode === 'ECONNABORTED') {
          return 'This is taking longer than expected. Please try again with smaller images or a different style.';
        }
        if (errMsg.toLowerCase().includes('too many requests') || errCode === 429) {
          return 'Too many requests right now. Please wait a moment and try again.';
        }
        if (errMsg.toLowerCase().includes('network') || errMsg.toLowerCase().includes('connection') || errCode === 'ECONNREFUSED' || errCode === 'ENOTFOUND') {
          return 'Unable to connect. Please check your internet connection and try again.';
        }
        if (errMsg.toLowerCase().includes('service is temporarily unavailable') || errCode === 503 || errCode === 500) {
          return 'Service temporarily unavailable. Please try again in a few moments.';
        }
        if (errMsg.toLowerCase().includes('no merged image') || errMsg.toLowerCase().includes('empty response')) {
          return 'We couldn\'t create the merged image. Please try again with different images.';
        }
        if (errMsg.toLowerCase().includes('api access denied') || errMsg.toLowerCase().includes('api key')) {
          return 'Service temporarily unavailable. Please try again in a few moments.';
        }
        if (errMsg.toLowerCase().includes('invalid request')) {
          return 'There was an issue with your images. Please make sure both images are valid and try again.';
        }
        
        // Default friendly message
        if (errMsg && !errMsg.includes('ECONN') && !errMsg.includes('timeout') && !errMsg.includes('network')) {
          // If it's already a readable message, use it but simplify if needed
          return errMsg.replace(/API error|Failed to|Error:/gi, '').trim() || 'Unable to merge your images right now. Please try again.';
        }
        
        return 'Unable to merge your images right now. Please try again in a moment.';
      };
      
      const errorMessage = getUserFriendlyError(err);
      toast.error(errorMessage);
      setMergeError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Download merged result
  const handleDownload = async () => {
    if (!resultImage) return;
    try {
      let downloadUrl = resultImage;
      let filename = `merged-image-${Date.now()}.png`;
      
      // If it's an HTTP/HTTPS URL, fetch it first
      if (resultImage.startsWith('http://') || resultImage.startsWith('https://')) {
        const resp = await fetch(resultImage);
        const blob = await resp.blob();
        downloadUrl = window.URL.createObjectURL(blob);
      }
      
      // Create download link with proper attributes to prevent tab opening
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.target = '_self'; // Prevent opening in new tab
      link.style.display = 'none'; // Hide the link
      link.setAttribute('download', filename); // Ensure download attribute is set
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      
      // Clean up after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        if (downloadUrl !== resultImage && downloadUrl.startsWith('blob:')) {
          window.URL.revokeObjectURL(downloadUrl);
        }
      }, 100);
      
      toast.success('Image downloaded successfully!');
    } catch (e) {
      console.error('Download failed', e);
      toast.error('Failed to download image. Please try again.');
    }
  };

  return (
    <div className="App merge-page">
      <AppHeader 
        credits={walletInfo.credits} 
        generated={walletInfo.totalSpent || 0} 
        user={user} 
        onLogout={handleLogout}
        onRecharge={() => setShowRechargeModal(true)}
      />
      
      <RechargeModal 
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
        onSuccess={() => {
          setShowRechargeModal(false);
          // Refresh wallet info
          const token = localStorage.getItem('authToken');
          if (token) {
            axios.get(`${BACKEND_URL}/api/wallet`, { headers: { Authorization: `Bearer ${token}` } })
              .then(res => {
                if (res.data?.success) setWalletInfo(res.data.data);
              })
              .catch(err => console.error('Wallet refresh error:', err));
          }
        }}
      />

      <main className="main-content">
      <div className="container merge-container" style={{ padding: '1rem 0' }}>
      <div className="content-grid">
        <div className="simple-layout">
          <div className="upload-section-wrapper">
            <div className="upload-card minimal-card">
              <div className="card-header"><h3 className="card-title">Jewelry Image</h3></div>
              <div {...dzFirst.getRootProps()} className={`upload-area minimal-upload ${dzFirst.isDragActive ? 'active' : ''}`} style={{ position: 'relative', cursor: 'pointer' }}>
                <input {...dzFirst.getInputProps()} ref={firstInputRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 1000, display: 'block' }} />
                {firstPreview ? (
                  <div className="preview-wrapper"><img className="preview-img" alt="A" src={firstPreview} /></div>
                ) : (
                  <div className="upload-content"><div className="upload-icon">📷</div><p className="upload-text">Click here or drag your jewelry photo</p>
                  <p className="upload-hint">Supports: JPG, PNG (up to 10MB)</p></div>
                  
                )}
              </div>
            </div>
            <div className="upload-card minimal-card">
              <div className="card-header"><h3 className="card-title">Real Human Model Image</h3></div>
              <div {...dzSecond.getRootProps()} className={`upload-area minimal-upload ${dzSecond.isDragActive ? 'active' : ''}`} style={{ position: 'relative', cursor: 'pointer' }}>
                <input {...dzSecond.getInputProps()} ref={secondInputRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 1000, display: 'block' }} />
                {secondPreview ? (
                  <div className="preview-wrapper"><img className="preview-img" alt="B" src={secondPreview} /></div>
                ) : (
                  <div className="upload-content"><div className="upload-icon">📷</div><p className="upload-text">Click or drag real human model photo</p>
                  <p className="upload-hint">Supports: JPG, PNG (up to 10MB)</p></div>
                )}
              </div>
            </div>

            {/* Controls similar to first page */}
            <div className="input-row minimal-input-row">
              <div className="input-group">
                <label htmlFor="merge-category-select" className="input-label">What type of jewelry is this?
                  <span className="help-text">Select the category</span>
                </label>
                <select
                  id="merge-category-select"
                  className="category-select"
                  value={jewelryCategory}
                  onChange={(e)=>setJewelryCategory(e.target.value)}
                >
                  <option value="">-- Select Type --</option>
                  <option value="Necklaces / Necklace Sets">Necklaces</option>
                  <option value="Earrings">Earrings</option>
                  <option value="Rings">Rings</option>
                  <option value="Bracelets">Bracelets</option>
                  <option value="Bangles">Bangles</option>
                  <option value="Pendants">Pendants</option>
                  <option value="Chains">Chains</option>
                  <option value="Anklets / Payal">Anklets</option>
                  <option value="Brooches">Brooches</option>
                  <option value="Nose Rings / Nose Pins">Nose Pins</option>
                  <option value="Mangalsutra">Mangalsutra</option>
                  <option value="Waist Belts / Vaddanam / Kamarbandh">Waist Belts</option>
                  <option value="Maang Tikka / Head Jewelry">Head Jewelry</option>
                  <option value="Hair Accessories">Hair Accessories</option>
                </select>
              </div>
              {/* AI-only merge: no manual placement controls */}
              <div className="input-group">
                <label htmlFor="merge-style-select" className="input-label">Photography Style
                  <span className="help-text">Choose how you want it to look</span>
                </label>
                <select id="merge-style-select" className="style-select" value={style} onChange={(e)=>setStyle(e.target.value)}>
                  <option value="realistic">Professional</option>
                  <option value="vintage">Vintage</option>
                  <option value="modern">Modern</option>
                  <option value="luxury">Luxury</option>
                  <option value="minimalist">Simple & Clean</option>
                  <option value="artistic">Artistic</option>
                </select>
              </div>
              <div className="button-group">
                <button className="generate-btn" onClick={handleGenerate} disabled={isGenerating || !firstFile || !secondFile || (walletInfo.credits <= 0)}>
                  {isGenerating ? 'Merging…' : <>✨ Merge Images <span className="credit-info">(Costs {mergeCost} credits)</span></>}
                </button>
              </div>
            </div>

            <div className="generated-card minimal-card result-section">
              <h3 className="card-title">Merged Result</h3>
              
              {isGenerating && (
                <div className="loading-area">
                  <div className="loading-spinner-large"></div>
                  <p className="loading-text-main">Merging your images...</p>
                  <p className="loading-text-sub">This may take 30-60 seconds</p>
                </div>
              )}

              {mergeError && !isGenerating && (
                <div className="error-display-area">
                  <div className="error-icon">⚠️</div>
                  <p className="error-message-text">{mergeError}</p>
                  <button 
                    className="error-dismiss-btn"
                    onClick={() => setMergeError(null)}
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {resultImage && !isGenerating && !mergeError && (
                <div className="result-area">
                  <img alt="Merged" src={resultImage} className="result-img" />
                  <div className="result-actions-minimal">
                    <button className="download-btn-minimal" onClick={handleDownload}>📥 Download Image</button>
                  </div>
                </div>
              )}

              {!resultImage && !isGenerating && !mergeError && (
                <div className="empty-area">
                  <div className="empty-icon-large">🖼️</div>
                  <p className="empty-text-main">Your merged image will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
      </main>
      <Footer />
    </div>
  );
};

export default MergePage;


